/**
 * Trace ids (D-259): one id links the screen, its requests and its errors. The format is the W3C trace-id
 * (32 lowercase hex characters, not all zero) so the same id can later travel in a `traceparent` header to
 * OpenTelemetry (ADR-06 §2.1). The browser creates one per screen visit and sends it on every request as
 * `x-faylasuf-trace`; the server accepts a well-formed id and otherwise creates its own. Request ids remain the
 * server's own (never taken from the client, C17).
 *
 * Browser-safe: uses only the global `crypto.getRandomValues`.
 */
export const TRACE_HEADER = "x-faylasuf-trace";
export const SCREEN_HEADER = "x-faylasuf-screen";

const TRACE_RE = /^[0-9a-f]{32}$/;
const ZERO = "0".repeat(32);
const SCREEN_RE = /^[a-z0-9_./-]{1,64}$/;

export function isTraceId(v: unknown): v is string {
  return typeof v === "string" && TRACE_RE.test(v) && v !== ZERO;
}

export function newTraceId(): string {
  const bytes = new Uint8Array(16);
  for (;;) {
    globalThis.crypto.getRandomValues(bytes);
    let s = "";
    for (const b of bytes) s += b.toString(16).padStart(2, "0");
    if (s !== ZERO) return s;
  }
}

/** The client's trace id when well-formed, else a new one. */
export function traceFromHeader(v: string | string[] | undefined): string {
  const one = Array.isArray(v) ? v[0] : v;
  return isTraceId(one) ? one : newTraceId();
}

/** A screen name as the logs accept it (`wallets`, `customers/statement`), else undefined. */
export function screenFromHeader(v: string | string[] | undefined): string | undefined {
  const one = Array.isArray(v) ? v[0] : v;
  return typeof one === "string" && SCREEN_RE.test(one) ? one : undefined;
}

/** A trace-id prefix a person may type into a search (at least 8 hex characters). */
export function isTracePrefix(v: unknown): v is string {
  return typeof v === "string" && /^[0-9a-f]{8,32}$/.test(v);
}

/** The short form shown to a user next to an error message ("رقم التتبع"): the first 12 characters. */
export function shortTrace(traceId: string): string {
  return traceId.slice(0, 12);
}
